import React, { useState } from 'react';
import { WhatsAppTemplate, WhatsAppButton } from '../services/marketingApi';

interface TemplateEditorProps {
  template: WhatsAppTemplate;
  onSend: (editedTemplate: WhatsAppTemplate) => void;
  onCancel: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onSend, onCancel }) => {
  const [editedBody, setEditedBody] = useState(template.body);
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [tempBody, setTempBody] = useState(template.body);

  const getTemplateTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'IMAGE':
        return 'bg-sky-wash/40 text-deep-sky';
      case 'VIDEO':
        return 'bg-brick/10 text-brick';
      case 'CAROUSEL':
        return 'bg-sky-wash/40 text-deep-sky';
      case 'TEXT_HEADER':
        return 'bg-gold/15 text-amber-ink';
      case 'TEXT':
      default:
        return 'bg-cream text-charcoal';
    }
  };

  const handleEditBody = () => {
    setTempBody(editedBody);
    setIsEditingBody(true);
  };

  const handleSaveBody = () => {
    setEditedBody(tempBody);
    setIsEditingBody(false);
  };

  const handleCancelEdit = () => {
    setTempBody(editedBody);
    setIsEditingBody(false);
  };

  const handleSend = () => {
    const updatedTemplate: WhatsAppTemplate = {
      ...template,
      body: editedBody
    };
    onSend(updatedTemplate);
  };

  const renderButton = (button: WhatsAppButton, index: number) => {
    return (
      <div
        key={index}
        className="px-4 py-2 border border-sketch rounded-lg text-sm text-center text-forest hover:bg-cream transition-colors flex items-center justify-center gap-2"
      >
        {button.type === 'URL' && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        )}
        {button.type === 'PHONE_NUMBER' && (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        )}
        <span>{button.text}</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />

      {/* Slide-over Panel */}
      <div className="relative w-full max-w-md bg-warm-white border-l border-sketch flex flex-col h-full animate-slide-in-right">
        {/* Header */}
        <div className="bg-forest p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onCancel} className="text-warm-white hover:text-warm-white/80">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <p className="text-warm-white font-medium">Template Preview</p>
              <p className="text-warm-white/70 text-sm">{template.display_name}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 bg-[#e5ddd5]">
          <div className="max-w-md mx-auto">
            {/* Template Type Badge */}
            <div className="mb-4">
              <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${getTemplateTypeBadgeColor(template.template_type)}`}>
                {template.template_type}
              </span>
            </div>

            {/* Template Message Card */}
            <div className="bg-warm-white rounded-lg shadow-sketch overflow-hidden">
              {/* Header Media (for IMAGE, VIDEO, CAROUSEL) */}
              {(template.template_type === 'IMAGE' || template.template_type === 'VIDEO') && template.header_media_url && (
                <div className="relative h-[140px] bg-charcoal/10">
                  <img
                    src={template.header_media_url}
                    alt="Template header"
                    className="w-full h-full object-cover"
                  />
                  {template.template_type === 'VIDEO' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-12 h-12 bg-warm-white/90 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-charcoal ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Header Text (for TEXT_HEADER) */}
              {template.template_type === 'TEXT_HEADER' && template.header_text && (
                <div className="p-4 border-b border-sketch">
                  <h3 className="font-semibold text-charcoal">{template.header_text}</h3>
                </div>
              )}

              {/* Body Section */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-sm font-semibold text-charcoal">Message</h4>
                  {!isEditingBody && (
                    <button
                      onClick={handleEditBody}
                      className="text-xs text-forest hover:underline flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  )}
                </div>

                {isEditingBody ? (
                  <div>
                    <textarea
                      value={tempBody}
                      onChange={(e) => setTempBody(e.target.value)}
                      className="w-full p-2 border border-sketch rounded text-sm text-charcoal focus:outline-none focus:border-forest"
                      rows={6}
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={handleSaveBody}
                        className="px-3 py-1 bg-forest text-warm-white rounded text-xs hover:bg-forest/80 transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-charcoal/10 text-charcoal rounded text-xs hover:bg-charcoal/20 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-charcoal whitespace-pre-wrap">{editedBody}</p>
                )}
              </div>

              {/* Footer */}
              {template.footer && (
                <div className="px-4 pb-2">
                  <p className="text-xs text-graphite">{template.footer}</p>
                </div>
              )}

              {/* Buttons */}
              {template.buttons && template.buttons.length > 0 && (
                <div className="p-4 pt-2 space-y-2">
                  {template.buttons.map((button, index) => renderButton(button, index))}
                </div>
              )}

              {/* Carousel Cards Preview */}
              {template.template_type === 'CAROUSEL' && template.carousel_cards && template.carousel_cards.length > 0 && (
                <div className="p-4 border-t border-sketch">
                  <div className="mb-2">
                    <h4 className="text-sm font-semibold text-charcoal mb-1">Carousel Cards</h4>
                    <p className="text-xs text-graphite">Cards cannot be edited. Only message text is editable.</p>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {template.carousel_cards.map((card, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 w-40 border border-sketch rounded-lg overflow-hidden bg-warm-white"
                      >
                        {card.header_media_url && (
                          <div className="h-24 bg-charcoal/10">
                            <img
                              src={card.header_media_url}
                              alt={`Card ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-2">
                          {card.title && (
                            <h5 className="text-xs font-semibold text-charcoal mb-1 truncate">
                              {card.title}
                            </h5>
                          )}
                          {card.body && (
                            <p className="text-xs text-graphite line-clamp-2 mb-2">
                              {card.body}
                            </p>
                          )}
                          {card.buttons && card.buttons.length > 0 && (
                            <div className="space-y-1">
                              {card.buttons.map((button, btnIndex) => (
                                <div
                                  key={btnIndex}
                                  className="text-xs text-forest text-center py-1 border border-sketch rounded truncate"
                                >
                                  {button.text}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="p-4 bg-warm-white border-t border-sketch flex items-center justify-between">
          <button
            onClick={onCancel}
            className="p-2 text-graphite hover:text-charcoal hover:bg-cream rounded-full transition-colors"
            title="Cancel"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <button
            onClick={handleSend}
            disabled={isEditingBody}
            className="px-6 py-2 bg-forest text-warm-white rounded-full hover:bg-forest/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Send Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
